import { IonModal } from "@ionic/react";
import { useFormik } from "formik";
import { CSSProperties, FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import * as Yup from "yup";
import { TextField } from "../../components/atoms/LoginTextField";
import { useAuth } from "../../contexts/AuthContext";
import { useConfig } from "../../contexts/ConfigContext";
import useToast from "../../hooks/useToast";
import { requestNicknameChange } from "../../services/user.service";

interface Props {
  isOpen: boolean;
  onDismiss(): void;
}

interface ChangeNicknameFormType {
  newNickname: string;
  currentPassword: string;
}

const changeNicknameSchema = Yup.object().shape({
  newNickname: Yup.string().trim().required("common_all_fields_required"),
  currentPassword: Yup.string().required("common_all_fields_required"),
});

const ChangeNicknameModal: FC<Props> = ({ isOpen, onDismiss }) => {
  const { t } = useTranslation();
  const { getCurrent } = useAuth();
  const { study } = useConfig();
  const present = useToast();
  const [isError, setIsError] = useState(false);
  const [isNewNicknameFieldUnlocked, setIsNewNicknameFieldUnlocked] = useState(false);
  const [isCurrentPasswordFieldUnlocked, setIsCurrentPasswordFieldUnlocked] = useState(false);

  const handleSubmit = async (values: ChangeNicknameFormType) => {
    try {
      setIsError(false);

      await requestNicknameChange(
        { studyId: study.id },
        {
          new_nickname: values.newNickname.trim(),
          current_password: values.currentPassword,
        }
      );

      await getCurrent();
      present(t("profile_change_nickname_success"));
      handleDismiss();
    } catch (error: any) {
      setIsError(true);

      if (error?.response?.data?.message === "Nickname already exists") {
        present(t("home_inscription_error_NICKNAME_EXIST"));
        return;
      }

      if (error?.response?.status === 401) {
        present(t("profile_change_nickname_invalid_password"));
        return;
      }

      if (error?.response?.data?.message === "New nickname must be different from current nickname") {
        present(t("profile_change_nickname_same_nickname"));
        return;
      }

      present(t("profile_change_nickname_error"));
    }
  };

  const { values, touched, errors, submitForm, setFieldValue, resetForm } =
    useFormik<ChangeNicknameFormType>({
      initialValues: {
        newNickname: "",
        currentPassword: "",
      },
      onSubmit: handleSubmit,
      validationSchema: changeNicknameSchema,
    });

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    resetForm();
    setIsError(false);
    setIsNewNicknameFieldUnlocked(false);
    setIsCurrentPasswordFieldUnlocked(false);
  }, [isOpen, resetForm]);

  const handleDismiss = () => {
    resetForm();
    setIsError(false);
    setIsNewNicknameFieldUnlocked(false);
    setIsCurrentPasswordFieldUnlocked(false);
    onDismiss();
  };

  return (
    <IonModal isOpen={isOpen} backdropDismiss={true} onWillDismiss={handleDismiss}>
      <Container onClick={handleDismiss}>
        <Card
          as="form"
          autoComplete="off"
          onSubmit={(event) => {
            event.preventDefault();
            submitForm();
          }}
          onClick={(event) => event.stopPropagation()}
        >
          <Title>{t("profile_change_nickname_title")}</Title>
          <Description>{t("profile_change_nickname_description")}</Description>

          <TextField
            value={values.newNickname}
            type="text"
            error={touched.newNickname && errors.newNickname}
            onChange={(value) => setFieldValue("newNickname", value)}
            label={t("profile_pseudonym")}
            placeholder={t("profile_pseudonym")}
            autoComplete="off"
            inputProps={{
              name: "profile-nickname-value",
              readOnly: !isNewNicknameFieldUnlocked,
              onFocus: () => setIsNewNicknameFieldUnlocked(true),
              onPointerDown: () => setIsNewNicknameFieldUnlocked(true),
              autoCapitalize: "none",
              autoCorrect: "off",
              spellCheck: false,
              "data-lpignore": "true",
              "data-1p-ignore": "true",
              "data-bwignore": "true",
              "data-form-type": "other",
            }}
          />

          <TextField
            value={values.currentPassword}
            type="text"
            error={touched.currentPassword && errors.currentPassword}
            onChange={(value) => setFieldValue("currentPassword", value)}
            label={t("profile_password")}
            placeholder={t("profile_password")}
            autoComplete="off"
            inputProps={{
              name: "profile-nickname-verification-value",
              readOnly: !isCurrentPasswordFieldUnlocked,
              onFocus: () => setIsCurrentPasswordFieldUnlocked(true),
              onPointerDown: () => setIsCurrentPasswordFieldUnlocked(true),
              autoCapitalize: "none",
              autoCorrect: "off",
              spellCheck: false,
              style: { WebkitTextSecurity: "disc" } as CSSProperties,
              "data-lpignore": "true",
              "data-1p-ignore": "true",
              "data-bwignore": "true",
              "data-form-type": "other",
            }}
          />

          {isError && <Error>{t("profile_change_nickname_error")}</Error>}

          <Buttons>
            <ModalButton onClick={handleDismiss}>{t("common_cancel")}</ModalButton>
            <ModalButton onClick={submitForm}>{t("common_next")}</ModalButton>
          </Buttons>
        </Card>
      </Container>
    </IonModal>
  );
};

const Container = styled.div`
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const Card = styled.div`
  width: 100%;
  background-color: white;
  border-radius: 10px;
  padding: 20px;
`;

const Title = styled.div`
  font-size: 18px;
  font-weight: bold;
  text-align: center;
  margin-bottom: 10px;
`;

const Description = styled.p`
  color: #5f5f5f;
  font-size: 14px;
  line-height: 1.5;
  margin: 0 0 10px;
  text-align: center;
`;

const Error = styled.div`
  color: ${(props) => props.theme.danger.main};
  margin-top: 10px;
  font-size: 15px;
`;

const Buttons = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 20px;
`;

const ModalButton = styled.div`
  padding: 10px 20px;
  color: ${(props) => props.theme.primary.main};
`;

export default ChangeNicknameModal;